<template>
    <transition name="emp-bulk-fade">
        <base-modal v-if="visible" fixed-footer :fullscreen="bulkMaximized" :zoom-animation="bulkZoomAnim"
            width="640px" height="85vh" @close="handleClose">
            <template #headerActions>
                <button type="button"
                    class="base-modal-control-btn base-modal-control-btn--expand emp-bulk-modal-expand-btn"
                    :aria-label="bulkMaximized ? 'Exit full screen' : 'Full screen'" @click="toggleBulkMaximize">
                    <font-awesome-icon :icon="['fas', bulkMaximized ? 'compress' : 'expand']" />
                </button>
            </template>
            <template #header>
                <div class="emp-bulk-modal-header emp-bulk-modal-header--with-controls"
                    :class="{ 'emp-bulk-modal-header--fullscreen': bulkMaximized }">
                    <div class="emp-bulk-modal-header-inner">
                        <div class="emp-bulk-modal-header-icon-wrap">
                            <font-awesome-icon :icon="headerIcon" class="emp-bulk-modal-header-icon"
                                :spin="bulkStep === 'uploading'" />
                        </div>
                        <div class="text-left min-w-0">
                            <h2 class="emp-bulk-modal-header-title">{{ headerTitle }}</h2>
                            <p class="emp-bulk-modal-header-sub">{{ headerSub }}</p>
                        </div>
                    </div>
                </div>
            </template>

            <div class="emp-bulk-modal-shell">
                <div class="emp-bulk-modal-scroll">
                    <input ref="bulkFileInput" type="file" accept=".xlsx,.xls" class="emp-bulk-modal-file-input"
                        @change="onBulkFileSelected" />

                    <!-- Select file -->
                    <template v-if="bulkStep === 'select'">
                        <div class="emp-bulk-modal-card">
                            <div class="emp-bulk-modal-card__head emp-bulk-modal-card__head--row">
                                <div>
                                    <h3 class="emp-bulk-modal-card__title">Excel file</h3>
                                    <p class="emp-bulk-modal-card__sub">
                                        Upload a completed template. Column headers must match exactly.
                                    </p>
                                </div>
                                <button type="button" class="emp-bulk-modal-add-btn" :disabled="bulkUploading"
                                    @click.stop="downloadBulkTemplate">
                                    <font-awesome-icon :icon="['fas', 'download']" />
                                    Template
                                </button>
                            </div>

                            <div class="emp-bulk-modal-dropzone" :class="{ 'emp-bulk-modal-dropzone--active': bulkDragOver }"
                                @click="triggerBulkFilePicker" @dragover.prevent="bulkDragOver = true"
                                @dragleave.prevent="bulkDragOver = false" @drop.prevent="onBulkFileDrop">
                                <font-awesome-icon :icon="['fas', 'cloud-upload-alt']" class="emp-bulk-modal-dropzone__icon" />
                                <p class="emp-bulk-modal-dropzone__title">Drop your Excel file here, or click to browse</p>
                                <p class="emp-bulk-modal-dropzone__sub">Accepted formats: .xlsx, .xls</p>
                            </div>
                        </div>

                        <div class="emp-bulk-modal-card">
                            <div class="emp-bulk-modal-card__head">
                                <h3 class="emp-bulk-modal-card__title">Column requirements</h3>
                                <p class="emp-bulk-modal-card__sub">
                                    Required: STUDENT ID, EID, EMPLOYER NAME, DATA SOURCE.
                                    Optional: POSITION, JOINING DATE.
                                </p>
                            </div>
                            <div v-if="employmentSourceLabels.length" class="emp-bulk-modal-tags">
                                <span class="emp-bulk-modal-tags__label">Valid DATA SOURCE values</span>
                                <div class="emp-bulk-modal-tags__list">
                                    <span v-for="(label, idx) in employmentSourceLabels" :key="'src-' + idx"
                                        class="emp-bulk-modal-tag">{{ label }}</span>
                                </div>
                            </div>
                        </div>
                    </template>

                    <!-- Review -->
                    <template v-else-if="bulkStep === 'review'">
                        <div class="emp-bulk-modal-card">
                            <div class="emp-bulk-modal-card__head">
                                <h3 class="emp-bulk-modal-card__title">Uploaded file</h3>
                                <p class="emp-bulk-modal-card__sub">Verify the file and parsed rows before confirming.</p>
                            </div>
                            <div class="emp-bulk-modal-file-card">
                                <div class="emp-bulk-modal-file-card__icon">
                                    <font-awesome-icon :icon="['fas', 'file-excel']" />
                                </div>
                                <div class="emp-bulk-modal-file-card__body">
                                    <p class="emp-bulk-modal-file-card__name">{{ bulkFileName }}</p>
                                    <p class="emp-bulk-modal-file-card__meta">
                                        {{ bulkValidRows.length }} valid row{{ bulkValidRows.length === 1 ? "" : "s" }}
                                        <span v-if="bulkValidationErrors.length">
                                            · {{ bulkValidationErrors.length }} issue{{ bulkValidationErrors.length === 1 ? "" : "s" }}
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div v-if="bulkValidationErrors.length" class="emp-bulk-modal-card emp-bulk-modal-card--error">
                            <div class="emp-bulk-modal-card__head">
                                <h3 class="emp-bulk-modal-card__title emp-bulk-modal-card__title--error">
                                    <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-2" />
                                    Fix before upload
                                </h3>
                            </div>
                            <div class="emp-bulk-modal-errors-list">
                                <div v-for="(err, idx) in bulkValidationErrors" :key="'bulk-err-' + idx"
                                    class="emp-bulk-modal-error-item">
                                    <span v-if="err.row" class="emp-bulk-modal-error-item__row">Row {{ err.row }}</span>
                                    <span>{{ err.message }}</span>
                                </div>
                            </div>
                        </div>

                        <div v-if="bulkValidRows.length" class="emp-bulk-modal-card">
                            <div class="emp-bulk-modal-card__head">
                                <h3 class="emp-bulk-modal-card__title">Review data</h3>
                                <p class="emp-bulk-modal-card__sub">{{ bulkValidRows.length }} row{{ bulkValidRows.length === 1 ? "" : "s" }} ready for import</p>
                            </div>
                            <div class="emp-bulk-modal-table-wrap"
                                :class="{ 'emp-bulk-modal-table-wrap--maximized': bulkMaximized }">
                                <table class="emp-bulk-modal-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Student ID</th>
                                            <th>EID</th>
                                            <th>Employer</th>
                                            <th>Position</th>
                                            <th>Joining Date</th>
                                            <th>Data Source</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(item, idx) in bulkValidRows" :key="'bulk-preview-' + idx">
                                            <td>{{ idx + 1 }}</td>
                                            <td>{{ item.student_id }}</td>
                                            <td>{{ item.eid }}</td>
                                            <td>{{ item.employer_name }}</td>
                                            <td>{{ item.position || "—" }}</td>
                                            <td>{{ item.joining_date || "—" }}</td>
                                            <td>{{ item.data_source }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div v-if="!bulkValidationErrors.length && bulkValidRows.length" class="emp-bulk-modal-ready">
                            <font-awesome-icon :icon="['fas', 'check-circle']" class="emp-bulk-modal-ready__icon" />
                            <div>
                                <p class="emp-bulk-modal-ready__title">Ready to upload {{ bulkValidRows.length }} record{{ bulkValidRows.length === 1 ? "" : "s" }}</p>
                                <p class="emp-bulk-modal-ready__sub">Employment details will be applied to matching student records.</p>
                            </div>
                        </div>
                    </template>

                    <!-- Uploading -->
                    <template v-else-if="bulkStep === 'uploading'">
                        <div class="emp-bulk-modal-card">
                            <div class="emp-bulk-modal-card__head">
                                <h3 class="emp-bulk-modal-card__title">Upload in progress</h3>
                                <p class="emp-bulk-modal-card__sub">Please wait while records are being updated.</p>
                            </div>
                            <div class="emp-bulk-modal-progress">
                                <div class="emp-bulk-modal-progress__head">
                                    <span class="emp-bulk-modal-progress__title">Processing rows</span>
                                    <span class="emp-bulk-modal-progress__percent">{{ bulkProgressPercent }}%</span>
                                </div>
                                <div class="emp-bulk-modal-progress__track">
                                    <div class="emp-bulk-modal-progress__fill" :style="{ width: bulkProgressPercent + '%' }"></div>
                                </div>
                                <p class="emp-bulk-modal-progress__sub">
                                    {{ bulkProgressCurrent }} / {{ bulkProgressTotal }}
                                </p>
                                <p v-if="bulkCurrentName" class="emp-bulk-modal-progress__name">{{ bulkCurrentName }}</p>
                            </div>
                        </div>
                    </template>

                    <!-- Done -->
                    <template v-else-if="bulkStep === 'done'">
                        <div class="emp-bulk-modal-summary">
                            <div class="emp-bulk-modal-summary__stat emp-bulk-modal-summary__stat--ok">
                                <p class="emp-bulk-modal-summary__value">{{ bulkSuccessCount }}</p>
                                <p class="emp-bulk-modal-summary__label">Successful</p>
                            </div>
                            <div class="emp-bulk-modal-summary__stat emp-bulk-modal-summary__stat--fail">
                                <p class="emp-bulk-modal-summary__value">{{ bulkFailCount }}</p>
                                <p class="emp-bulk-modal-summary__label">Failed</p>
                            </div>
                            <div class="emp-bulk-modal-summary__stat">
                                <p class="emp-bulk-modal-summary__value">{{ bulkProgressTotal }}</p>
                                <p class="emp-bulk-modal-summary__label">Total</p>
                            </div>
                        </div>

                        <div v-if="bulkUploadErrors.length" class="emp-bulk-modal-card emp-bulk-modal-card--error">
                            <div class="emp-bulk-modal-card__head">
                                <h3 class="emp-bulk-modal-card__title emp-bulk-modal-card__title--error">
                                    <font-awesome-icon :icon="['fas', 'times-circle']" class="mr-2" />
                                    Upload errors
                                </h3>
                            </div>
                            <div class="emp-bulk-modal-errors-list">
                                <div v-for="(err, idx) in bulkUploadErrors" :key="'bulk-up-err-' + idx"
                                    class="emp-bulk-modal-error-item">
                                    <span v-if="err.row" class="emp-bulk-modal-error-item__row">Row {{ err.row }}</span>
                                    <span>{{ err.message }}</span>
                                </div>
                            </div>
                        </div>
                    </template>

                    <p v-if="footerHint" class="emp-bulk-modal-hint">
                        <font-awesome-icon :icon="['fas', 'lightbulb']" class="emp-bulk-modal-hint-icon" />
                        {{ footerHint }}
                    </p>
                </div>

                <div class="emp-bulk-modal-footer" :class="{ 'emp-bulk-modal-footer--fullscreen': bulkMaximized }">
                    <div class="emp-bulk-modal-actions">
                        <template v-if="bulkStep === 'select'">
                            <button type="button" class="emp-bulk-modal-btn emp-bulk-modal-btn-secondary"
                                @click="handleClose">
                                Cancel
                            </button>
                        </template>

                        <template v-else-if="bulkStep === 'review'">
                            <button type="button" class="emp-bulk-modal-btn emp-bulk-modal-btn-secondary"
                                :disabled="bulkUploading" @click="resetBulkSelection">
                                Change file
                            </button>
                            <button type="button" class="emp-bulk-modal-btn emp-bulk-modal-btn-primary"
                                :disabled="bulkUploading || !bulkValidRows.length || bulkValidationErrors.length > 0"
                                @click="startBulkUpload">
                                <font-awesome-icon :icon="['fas', 'upload']" class="mr-2" />
                                Confirm Upload
                            </button>
                        </template>

                        <template v-else-if="bulkStep === 'uploading'">
                            <button type="button" class="emp-bulk-modal-btn emp-bulk-modal-btn-secondary" disabled>
                                Cancel
                            </button>
                            <button type="button" class="emp-bulk-modal-btn emp-bulk-modal-btn-primary" disabled>
                                <font-awesome-icon :icon="['fas', 'spinner']" spin class="mr-2" />
                                Uploading…
                            </button>
                        </template>

                        <template v-else-if="bulkStep === 'done'">
                            <button type="button" class="emp-bulk-modal-btn emp-bulk-modal-btn-primary"
                                @click="handleClose">
                                Done
                            </button>
                        </template>
                    </div>
                </div>
            </div>
        </base-modal>
    </transition>
</template>

<script>
import gql from "graphql-tag";
import xlsx from "xlsx/dist/xlsx.full.min.js";

const BULK_TEMPLATE_FILENAME = "employment-dashboard-bulk-upload-template.xlsx";
const BULK_TEMPLATE_URL = require("../../../assets/templates/employment-dashboard-bulk-upload-template.xlsx");

const REQUIRED_BULK_HEADERS = [
    "STUDENT ID",
    "EID",
    "EMPLOYER NAME",
    "POSITION",
    "JOINING DATE",
    "DATA SOURCE",
];

const EMPLOYMENT_SOURCE_OPTIONS_QUERY = gql`
    query EmploymentSourceOptionsActive {
        employmentSourceOptionsActive {
            code
            label
        }
    }
`;

const BULK_UPLOAD_EMPLOYMENT_MUTATION = gql`
    mutation BulkUploadEmployment($input: BulkUploadEmploymentInput!) {
        bulkUploadEmployment(input: $input) {
            error
            error_code
            message
            data {
                success_count
                fail_count
                total
                last_updated
                bulk_upload_history_id
                results {
                    row
                    student_id
                    eid
                    error
                    message
                }
            }
        }
    }
`;

function normalizeBulkHeader(value) {
    if (value === null || value === undefined) return "";
    return String(value)
        .replace(/\u00a0/g, " ")
        .trim()
        .replace(/\s+/g, " ")
        .toUpperCase();
}

function bulkCellToString(value) {
    if (value === null || value === undefined) return "";
    if (typeof value === "number") {
        if (Math.floor(value) === value) return String(Math.trunc(value));
        return String(value);
    }
    return String(value).replace(/\u00a0/g, " ").trim();
}

function bulkCellAt(row, index) {
    if (index === undefined) return "";
    return bulkCellToString(row[index]);
}

function parseBulkJoiningDate(value) {
    var raw = bulkCellToString(value);
    if (!raw) return { value: null, error: null };

    if (/^\d+(\.\d+)?$/.test(raw)) {
        var excelEpoch = new Date(Date.UTC(1899, 11, 30));
        var days = parseFloat(raw);
        var parsedExcel = new Date(excelEpoch.getTime() + days * 86400000);
        if (!isNaN(parsedExcel.getTime())) {
            return {
                value: parsedExcel.toISOString().slice(0, 10),
                error: null,
            };
        }
    }

    var patterns = [
        /^(\d{4})-(\d{1,2})-(\d{1,2})$/,
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
        /^(\d{1,2})-(\d{1,2})-(\d{4})$/,
    ];

    for (var i = 0; i < patterns.length; i++) {
        var match = raw.match(patterns[i]);
        if (match) {
            var y;
            var m;
            var d;
            if (patterns[i].source.indexOf("(\\d{4})") === 1) {
                y = parseInt(match[1], 10);
                m = parseInt(match[2], 10);
                d = parseInt(match[3], 10);
            } else {
                d = parseInt(match[1], 10);
                m = parseInt(match[2], 10);
                y = parseInt(match[3], 10);
            }
            if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
                var mm = m < 10 ? "0" + m : String(m);
                var dd = d < 10 ? "0" + d : String(d);
                return { value: y + "-" + mm + "-" + dd, error: null };
            }
        }
    }

    var fallback = new Date(raw);
    if (!isNaN(fallback.getTime())) {
        return { value: fallback.toISOString().slice(0, 10), error: null };
    }

    return { value: null, error: "Invalid JOINING DATE format." };
}

function generateUploadSessionId() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (char) {
        var random = Math.random() * 16 | 0;
        var value = char === "x" ? random : (random & 0x3 | 0x8);
        return value.toString(16);
    });
}

export default {
    name: "EmploymentBulkUploadModal",
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["close", "uploaded"],
    data() {
        return {
            bulkStep: "select",
            bulkDragOver: false,
            bulkFileName: "",
            bulkFile: null,
            bulkUploadSessionId: "",
            bulkValidRows: [],
            bulkValidationErrors: [],
            bulkUploading: false,
            bulkProgressCurrent: 0,
            bulkProgressTotal: 0,
            bulkCurrentName: "",
            bulkSuccessCount: 0,
            bulkFailCount: 0,
            bulkUploadErrors: [],
            bulkMaximized: false,
            bulkZoomAnim: "",
            bulkZoomTimer: null,
            bulkLastUpdated: "",
            employmentSourceOptions: [],
        };
    },
    computed: {
        bulkProgressPercent() {
            if (!this.bulkProgressTotal) return 0;
            return Math.min(100, Math.round((this.bulkProgressCurrent / this.bulkProgressTotal) * 100));
        },
        headerTitle() {
            if (this.bulkStep === "review") return "Review Upload";
            if (this.bulkStep === "uploading") return "Uploading Employment Data";
            if (this.bulkStep === "done") return "Upload Complete";
            return "Bulk Upload Employment";
        },
        headerSub() {
            if (this.bulkStep === "review") return "Check parsed rows and fix any validation issues before confirming.";
            if (this.bulkStep === "uploading") return "Applying employment updates to student records.";
            if (this.bulkStep === "done") return "Summary of successful and failed row updates.";
            return "Download the template, fill in employment data, then upload your Excel file.";
        },
        headerIcon() {
            if (this.bulkStep === "review") return ["fas", "clipboard-check"];
            if (this.bulkStep === "uploading") return ["fas", "spinner"];
            if (this.bulkStep === "done") return ["fas", "check-circle"];
            return ["fas", "file-excel"];
        },
        footerHint() {
            if (this.bulkStep === "select") {
                return "Download the template first, then upload the completed file. DATA SOURCE must match one of the allowed labels.";
            }
            if (this.bulkStep === "review" && this.bulkValidationErrors.length) {
                return "Resolve all validation issues or choose a different file before confirming the upload.";
            }
            if (this.bulkStep === "review" && this.bulkValidRows.length) {
                return "Confirming will update student employment records, profiles, and employer information.";
            }
            if (this.bulkStep === "done" && this.bulkFailCount > 0) {
                return "Failed rows were not updated. Review the errors above and re-upload corrected rows if needed.";
            }
            return "";
        },
        employmentSourceLabels() {
            var options = this.employmentSourceOptions || [];
            var labels = [];
            for (var i = 0; i < options.length; i++) {
                if (options[i] && options[i].label) {
                    labels.push(options[i].label);
                }
            }
            return labels;
        },
        employmentSourceLookup() {
            var options = this.employmentSourceOptions || [];
            var lookup = {};
            for (var i = 0; i < options.length; i++) {
                var opt = options[i];
                if (!opt) continue;
                if (opt.label) {
                    lookup[normalizeBulkHeader(opt.label)] = opt;
                }
                if (opt.code) {
                    lookup[normalizeBulkHeader(opt.code)] = opt;
                }
            }
            return lookup;
        },
    },
    watch: {
        visible: function (val) {
            if (val) {
                this.resetState();
                this.loadEmploymentSourceOptions();
            }
        },
    },
    methods: {
        resetState() {
            this.bulkStep = "select";
            this.bulkDragOver = false;
            this.bulkFileName = "";
            this.bulkFile = null;
            this.bulkUploadSessionId = "";
            this.bulkValidRows = [];
            this.bulkValidationErrors = [];
            this.bulkUploading = false;
            this.bulkProgressCurrent = 0;
            this.bulkProgressTotal = 0;
            this.bulkCurrentName = "";
            this.bulkSuccessCount = 0;
            this.bulkFailCount = 0;
            this.bulkUploadErrors = [];
            this.bulkMaximized = false;
            this.bulkZoomAnim = "";
            this.bulkLastUpdated = "";
            this.clearBulkZoomTimer();

            if (this.$refs && this.$refs.bulkFileInput) {
                this.$refs.bulkFileInput.value = null;
            }
        },
        handleClose() {
            var hadSuccess = this.bulkSuccessCount > 0;
            this.bulkMaximized = false;
            this.bulkZoomAnim = "";
            this.bulkLastUpdated = "";
            this.clearBulkZoomTimer();
            this.$emit("close");
            if (hadSuccess) {
                this.$emit("uploaded", { lastUpdated: this.bulkLastUpdated });
            }
        },
        resetBulkSelection() {
            this.bulkStep = "select";
            this.bulkDragOver = false;
            this.bulkFileName = "";
            this.bulkFile = null;
            this.bulkUploadSessionId = "";
            this.bulkValidRows = [];
            this.bulkValidationErrors = [];
            this.bulkUploadErrors = [];

            if (this.$refs && this.$refs.bulkFileInput) {
                this.$refs.bulkFileInput.value = null;
            }
        },
        clearBulkZoomTimer() {
            if (this.bulkZoomTimer) {
                clearTimeout(this.bulkZoomTimer);
                this.bulkZoomTimer = null;
            }
        },
        toggleBulkMaximize() {
            var self = this;
            var goingFullscreen = !this.bulkMaximized;
            this.bulkZoomAnim = "";
            this.clearBulkZoomTimer();
            this.$nextTick(function () {
                self.bulkMaximized = goingFullscreen;
                self.bulkZoomAnim = goingFullscreen ? "in" : "out";
                self.bulkZoomTimer = setTimeout(function () {
                    self.bulkZoomAnim = "";
                    self.bulkZoomTimer = null;
                }, 460);
            });
        },
        loadEmploymentSourceOptions() {
            var self = this;
            this.$apollo.query({
                query: EMPLOYMENT_SOURCE_OPTIONS_QUERY,
                fetchPolicy: "network-only",
            }).then(function (res) {
                var options = res && res.data && res.data.employmentSourceOptionsActive
                    ? res.data.employmentSourceOptionsActive
                    : [];
                self.employmentSourceOptions = options;
            }).catch(function () {
                self.employmentSourceOptions = [];
            });
        },
        triggerBulkFilePicker() {
            if (this.$refs && this.$refs.bulkFileInput) {
                this.$refs.bulkFileInput.click();
            }
        },
        downloadBulkTemplate() {
            try {
                var link = document.createElement("a");
                link.href = BULK_TEMPLATE_URL;
                link.download = BULK_TEMPLATE_FILENAME;
                link.target = "_blank";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (e) {
                console.error(e);
            }
        },
        onBulkFileDrop(event) {
            this.bulkDragOver = false;
            if (!event || !event.dataTransfer || !event.dataTransfer.files) return;
            var files = event.dataTransfer.files;
            if (!files.length) return;
            var file = files[0];
            if (file) {
                this.parseBulkXlsxFile(file);
            }
        },
        onBulkFileSelected(event) {
            var input = event && event.target ? event.target : null;
            if (!input || !input.files || !input.files.length) return;
            var file = input.files[0];
            if (file) {
                this.parseBulkXlsxFile(file);
            }
        },
        parseBulkXlsxFile(file) {
            var self = this;

            this.bulkStep = "review";
            this.bulkFileName = file && file.name ? file.name : "Uploaded file";
            this.bulkFile = file || null;
            this.bulkUploadSessionId = "";
            this.bulkValidRows = [];
            this.bulkValidationErrors = [];
            this.bulkUploadErrors = [];

            var reader = new FileReader();
            reader.onload = function (e) {
                try {
                    var data = e && e.target ? e.target.result : null;
                    if (!data) {
                        self.bulkValidationErrors = [{ row: 0, message: "Could not read Excel file." }];
                        return;
                    }

                    var workbook = xlsx.read(data, { type: "array" });
                    var sheetName = workbook.SheetNames && workbook.SheetNames.length ? workbook.SheetNames[0] : null;
                    if (!sheetName) {
                        self.bulkValidationErrors = [{ row: 0, message: "Excel sheet not found." }];
                        return;
                    }

                    var sheet = workbook.Sheets[sheetName];
                    var aoa = xlsx.utils.sheet_to_json(sheet, { header: 1, defval: "" });
                    var parsed = self.validateAndBuildBulkRows(aoa);

                    self.bulkValidRows = parsed.validRows;
                    self.bulkValidationErrors = parsed.errors;
                } catch (err) {
                    self.bulkValidationErrors = [
                        { row: 0, message: (err && err.message) ? err.message : "Failed to parse Excel file." },
                    ];
                }
            };

            reader.onerror = function () {
                self.bulkValidationErrors = [{ row: 0, message: "Failed to read Excel file." }];
            };

            reader.readAsArrayBuffer(file);
        },
        validateAndBuildBulkRows(aoa) {
            var errors = [];
            var validRows = [];
            var self = this;

            if (!aoa || !aoa.length) {
                return {
                    validRows: [],
                    errors: [{ row: 0, message: "The Excel file is empty." }],
                };
            }

            var headerRow = aoa[0] || [];
            var headerIndex = {};
            for (var i = 0; i < headerRow.length; i++) {
                var hn = normalizeBulkHeader(headerRow[i]);
                if (hn) {
                    headerIndex[hn] = i;
                }
            }

            for (var h = 0; h < REQUIRED_BULK_HEADERS.length; h++) {
                var requiredHeader = REQUIRED_BULK_HEADERS[h];
                if (headerIndex[requiredHeader] === undefined) {
                    errors.push({
                        row: 1,
                        message: "Invalid template. Missing required column: " + requiredHeader + ". Please download and use the official template.",
                    });
                    return { validRows: [], errors: errors };
                }
            }

            var idxStudentId = headerIndex["STUDENT ID"];
            var idxEid = headerIndex["EID"];
            var idxEmployer = headerIndex["EMPLOYER NAME"];
            var idxPosition = headerIndex["POSITION"];
            var idxJoiningDate = headerIndex["JOINING DATE"];
            var idxDataSource = headerIndex["DATA SOURCE"];

            for (var rowIndex = 1; rowIndex < aoa.length; rowIndex++) {
                var excelRow = rowIndex + 1;
                var row = aoa[rowIndex] || [];

                var rawStudentId = bulkCellAt(row, idxStudentId);
                var rawEid = bulkCellAt(row, idxEid);
                var rawEmployer = bulkCellAt(row, idxEmployer);
                var rawPosition = bulkCellAt(row, idxPosition);
                var rawJoiningDate = bulkCellAt(row, idxJoiningDate);
                var rawDataSource = bulkCellAt(row, idxDataSource);

                if (
                    rawStudentId === "" &&
                    rawEid === "" &&
                    rawEmployer === "" &&
                    rawPosition === "" &&
                    rawJoiningDate === "" &&
                    rawDataSource === ""
                ) {
                    continue;
                }

                if (!rawStudentId) {
                    errors.push({ row: excelRow, message: "STUDENT ID is required." });
                    continue;
                }
                if (!rawEid) {
                    errors.push({ row: excelRow, message: "EID is required." });
                    continue;
                }
                if (!rawEmployer) {
                    errors.push({ row: excelRow, message: "EMPLOYER NAME is required." });
                    continue;
                }
                if (!rawDataSource) {
                    errors.push({ row: excelRow, message: "DATA SOURCE is required." });
                    continue;
                }

                var sourceKey = normalizeBulkHeader(rawDataSource);
                var sourceMatch = self.employmentSourceLookup[sourceKey];
                if (!sourceMatch) {
                    var available = self.employmentSourceLabels.join(", ");
                    errors.push({
                        row: excelRow,
                        message: "Invalid DATA SOURCE \"" + rawDataSource + "\". Available options: " + available,
                    });
                    continue;
                }

                var joiningParsed = parseBulkJoiningDate(rawJoiningDate);
                if (joiningParsed.error) {
                    errors.push({ row: excelRow, message: joiningParsed.error });
                    continue;
                }

                validRows.push({
                    excelRow: excelRow,
                    student_id: rawStudentId,
                    eid: rawEid,
                    employer_name: rawEmployer,
                    position: rawPosition !== "" ? rawPosition : null,
                    joining_date: joiningParsed.value,
                    data_source: sourceMatch.label || rawDataSource,
                });
            }

            if (!validRows.length && !errors.length) {
                errors.push({ row: 0, message: "No valid rows found. Please add data rows in the template." });
            }

            return { validRows: validRows, errors: errors };
        },
        async startBulkUpload() {
            if (!this.bulkValidRows.length || this.bulkValidationErrors.length) {
                return;
            }

            if (!this.bulkFile) {
                this.bulkValidationErrors = [{
                    row: 0,
                    message: "Original Excel file is missing. Please select the file again.",
                }];
                this.bulkStep = "review";
                return;
            }

            this.bulkUploading = true;
            this.bulkStep = "uploading";
            this.bulkUploadSessionId = generateUploadSessionId();
            this.bulkProgressTotal = this.bulkValidRows.length;
            this.bulkProgressCurrent = 0;
            this.bulkCurrentName = "";
            this.bulkSuccessCount = 0;
            this.bulkFailCount = 0;
            this.bulkUploadErrors = [];
            this.bulkLastUpdated = "";

            var chunkSize = 100;
            var total = this.bulkValidRows.length;
            var offset = 0;

            try {
                while (offset < total) {
                    var chunk = this.bulkValidRows.slice(offset, offset + chunkSize);
                    var isFirstBatch = offset === 0;
                    var isFinalBatch = offset + chunk.length >= total;
                    if (chunk && chunk.length) {
                        var first = chunk[0];
                        this.bulkCurrentName = first && first.student_id ? first.student_id : "—";
                    }

                    var input = {
                        upload_session_id: this.bulkUploadSessionId,
                        original_filename: this.bulkFileName,
                        total_rows: total,
                        is_first_batch: isFirstBatch,
                        is_final_batch: isFinalBatch,
                        rows: chunk.map(function (r) {
                            return {
                                row: r.excelRow,
                                student_id: r.student_id,
                                eid: r.eid,
                                employer_name: r.employer_name,
                                position: r.position,
                                joining_date: r.joining_date,
                                data_source: r.data_source,
                            };
                        }),
                    };

                    if (isFirstBatch && this.bulkFile) {
                        input.file = this.bulkFile;
                    }

                    try {
                        var res = await this.$apollo.mutate({
                            mutation: BULK_UPLOAD_EMPLOYMENT_MUTATION,
                            variables: { input: input },
                        });

                        var root = res && res.data ? res.data.bulkUploadEmployment : null;
                        var payloadData = root && root.data ? root.data : null;

                        if (payloadData) {
                            this.bulkSuccessCount += payloadData.success_count || 0;
                            this.bulkFailCount += payloadData.fail_count || 0;

                            if (payloadData.last_updated) {
                                this.bulkLastUpdated = payloadData.last_updated;
                            }

                            if (payloadData.results && payloadData.results.length) {
                                for (var j = 0; j < payloadData.results.length; j++) {
                                    var rr = payloadData.results[j];
                                    if (rr && rr.error) {
                                        this.bulkUploadErrors.push({
                                            row: rr.row,
                                            message: rr.message || "Failed to update employment record",
                                        });
                                    }
                                }
                            }
                        } else {
                            var emptyPayloadMessage = (root && root.message) ? root.message : "Bulk upload failed";
                            for (var k = 0; k < chunk.length; k++) {
                                this.bulkUploadErrors.push({
                                    row: chunk[k] && chunk[k].excelRow ? chunk[k].excelRow : offset + k + 1,
                                    message: emptyPayloadMessage,
                                });
                                this.bulkFailCount += 1;
                            }
                        }
                    } catch (err) {
                        var catchMessage = (err && err.message) ? err.message : "Bulk upload failed";
                        for (var m = 0; m < chunk.length; m++) {
                            this.bulkUploadErrors.push({
                                row: chunk[m] && chunk[m].excelRow ? chunk[m].excelRow : offset + m + 1,
                                message: catchMessage,
                            });
                            this.bulkFailCount += 1;
                        }
                    }

                    this.bulkProgressCurrent = Math.min(total, offset + chunk.length);
                    offset += chunkSize;
                }

                this.bulkStep = "done";
            } finally {
                this.bulkUploading = false;
                this.bulkCurrentName = "";
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.emp-bulk-fade-enter-active,
.emp-bulk-fade-leave-active {
    transition: opacity 0.2s ease;
}

:deep(.base-modal-control-btn.emp-bulk-modal-expand-btn:hover),
:deep(.base-modal-control-btn.emp-bulk-modal-expand-btn:focus-visible) {
    background: #2563eb;
    border-color: #2563eb;
    color: #fff;
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.35);
}

.emp-bulk-fade-enter-from,
.emp-bulk-fade-leave-to {
    opacity: 0;
}

.emp-bulk-modal-header {
    padding: 1.25rem 3rem 1.25rem 1.5rem;
    background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 45%, #3b82f6 100%);
    color: #fff;
    border-radius: 1rem 1rem 0 0;
    box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.08);
}

.emp-bulk-modal-header--fullscreen {
    border-radius: 0;
}

.emp-bulk-modal-header--with-controls {
    padding-right: 5.5rem;
}

.emp-bulk-modal-header-inner {
    display: flex;
    align-items: center;
    gap: 0.875rem;
}

.emp-bulk-modal-header-icon-wrap {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.75rem;
    background: rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
}

.emp-bulk-modal-header-icon {
    font-size: 1.35rem;
    opacity: 0.95;
}

.emp-bulk-modal-header-title {
    font-size: 1.125rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin: 0;
    line-height: 1.3;
}

.emp-bulk-modal-header-sub {
    margin: 0.2rem 0 0 0;
    font-size: 0.8125rem;
    color: rgba(219, 234, 254, 0.95);
    line-height: 1.35;
}

.emp-bulk-modal-shell {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
}

.emp-bulk-modal-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 1.5rem;
    background: linear-gradient(180deg, #f8fafc 0%, #eff6ff 100%);
}

.emp-bulk-modal-file-input {
    display: none;
}

.emp-bulk-modal-footer {
    flex-shrink: 0;
    padding: 1rem 1.5rem 1.25rem;
    background: #fff;
    border-top: 1px solid #e2e8f0;
    box-shadow: 0 -8px 24px rgba(15, 23, 42, 0.06);
    border-radius: 0 0 1rem 1rem;
}

.emp-bulk-modal-footer--fullscreen {
    border-radius: 0;
}

.emp-bulk-modal-card {
    background: #fff;
    border-radius: 1rem;
    border: 1px solid #e2e8f0;
    box-shadow: 0 10px 40px rgba(15, 23, 42, 0.08), 0 2px 8px rgba(15, 23, 42, 0.04);
    padding: 1.25rem;
    margin-bottom: 1rem;
}

.emp-bulk-modal-card--error {
    border-color: rgba(239, 68, 68, 0.25);
    background: rgba(254, 242, 242, 0.35);
}

.emp-bulk-modal-card__head {
    margin-bottom: 1rem;
}

.emp-bulk-modal-card__head--row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
}

.emp-bulk-modal-card__title {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 700;
    color: #0f172a;
}

.emp-bulk-modal-card__title--error {
    color: #b91c1c;
    display: flex;
    align-items: center;
}

.emp-bulk-modal-card__sub {
    margin: 0.25rem 0 0 0;
    font-size: 0.75rem;
    color: #64748b;
    line-height: 1.4;
}

.emp-bulk-modal-add-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.5rem 0.85rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #1d4ed8;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 0.625rem;
    flex-shrink: 0;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;
}

.emp-bulk-modal-add-btn:hover {
    background: #dbeafe;
    border-color: #93c5fd;
}

.emp-bulk-modal-dropzone {
    border: 2px dashed #cbd5e1;
    background: #f8fafc;
    border-radius: 0.85rem;
    padding: 2rem 1rem;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.15s ease, background 0.15s ease;
}

.emp-bulk-modal-dropzone--active {
    border-color: #60a5fa;
    background: #eff6ff;
}

.emp-bulk-modal-dropzone__icon {
    font-size: 2rem;
    color: #2563eb;
}

.emp-bulk-modal-dropzone__title {
    margin: 0.75rem 0 0.25rem;
    font-size: 0.9375rem;
    font-weight: 700;
    color: #1e40af;
}

.emp-bulk-modal-dropzone__sub {
    margin: 0;
    font-size: 0.8125rem;
    color: #64748b;
}

.emp-bulk-modal-tags__label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 0.5rem;
}

.emp-bulk-modal-tags__list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
}

.emp-bulk-modal-tag {
    display: inline-block;
    padding: 0.2rem 0.55rem;
    border-radius: 9999px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    color: #1d4ed8;
    font-size: 0.75rem;
    font-weight: 600;
}

.emp-bulk-modal-file-card {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    padding: 0.85rem 1rem;
    border-radius: 0.75rem;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
}

.emp-bulk-modal-file-card__icon {
    color: #2563eb;
    font-size: 1.25rem;
    flex-shrink: 0;
}

.emp-bulk-modal-file-card__body {
    min-width: 0;
    flex: 1;
}

.emp-bulk-modal-file-card__name {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 700;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.emp-bulk-modal-file-card__meta {
    margin: 0.2rem 0 0;
    font-size: 0.8125rem;
    color: #64748b;
}

.emp-bulk-modal-errors-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-height: 160px;
    overflow-y: auto;
}

.emp-bulk-modal-error-item {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: baseline;
    font-size: 0.8125rem;
    color: #7f1d1d;
}

.emp-bulk-modal-error-item__row {
    font-weight: 800;
}

.emp-bulk-modal-table-wrap {
    max-height: 220px;
    overflow: auto;
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
}

.emp-bulk-modal-table-wrap--maximized {
    max-height: calc(100vh - 22rem);
}

.emp-bulk-modal-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 0.75rem;
}

.emp-bulk-modal-table th,
.emp-bulk-modal-table td {
    padding: 0.5rem 0.65rem;
    border-bottom: 1px solid #f1f5f9;
    text-align: left;
    white-space: nowrap;
}

.emp-bulk-modal-table th {
    background-color: #1e40af;
    color: #fff;
    font-weight: 700;
    position: sticky;
    top: 0;
    z-index: 2;
    background-clip: padding-box;
    border-bottom: 1px solid #1e3a8a;
}

.emp-bulk-modal-table th + th {
    box-shadow: -1px 0 0 0 #1e40af;
}

.emp-bulk-modal-ready {
    display: flex;
    gap: 0.85rem;
    align-items: flex-start;
    background: rgba(37, 99, 235, 0.08);
    border: 1px solid rgba(37, 99, 235, 0.2);
    border-radius: 0.9rem;
    padding: 0.9rem 1rem;
    margin-bottom: 1rem;
}

.emp-bulk-modal-ready__icon {
    margin-top: 0.1rem;
    color: #2563eb;
    font-size: 1.25rem;
    flex-shrink: 0;
}

.emp-bulk-modal-ready__title {
    margin: 0;
    font-weight: 700;
    color: #1e40af;
    line-height: 1.25;
}

.emp-bulk-modal-ready__sub {
    margin: 0.35rem 0 0;
    color: #475569;
    font-size: 0.8125rem;
}

.emp-bulk-modal-progress__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
}

.emp-bulk-modal-progress__title {
    font-size: 0.875rem;
    font-weight: 700;
    color: #0f172a;
}

.emp-bulk-modal-progress__percent {
    font-size: 0.875rem;
    font-weight: 800;
    color: #2563eb;
}

.emp-bulk-modal-progress__track {
    height: 10px;
    background: #e2e8f0;
    border-radius: 9999px;
    overflow: hidden;
}

.emp-bulk-modal-progress__fill {
    height: 10px;
    background: linear-gradient(90deg, #2563eb 0%, #3b82f6 100%);
    border-radius: 9999px;
    transition: width 0.2s ease;
}

.emp-bulk-modal-progress__sub {
    margin: 0.75rem 0 0;
    font-size: 0.8125rem;
    color: #64748b;
}

.emp-bulk-modal-progress__name {
    margin: 0.5rem 0 0;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #334155;
}

.emp-bulk-modal-summary {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 0.75rem;
    margin-bottom: 1rem;
}

.emp-bulk-modal-summary__stat {
    border: 1px solid #e2e8f0;
    background: #fff;
    border-radius: 0.9rem;
    padding: 0.85rem 1rem;
    text-align: center;
}

.emp-bulk-modal-summary__stat--ok {
    border-color: rgba(37, 99, 235, 0.25);
    background: rgba(239, 246, 255, 0.8);
}

.emp-bulk-modal-summary__stat--fail {
    border-color: rgba(239, 68, 68, 0.25);
    background: rgba(254, 242, 242, 0.8);
}

.emp-bulk-modal-summary__stat--ok .emp-bulk-modal-summary__value {
    color: #1d4ed8;
}

.emp-bulk-modal-summary__stat--fail .emp-bulk-modal-summary__value {
    color: #b91c1c;
}

.emp-bulk-modal-summary__value {
    margin: 0;
    font-size: 1.6rem;
    font-weight: 900;
    color: #0f172a;
}

.emp-bulk-modal-summary__label {
    margin: 0.25rem 0 0;
    font-size: 0.8125rem;
    font-weight: 700;
    color: #64748b;
}

.emp-bulk-modal-hint {
    margin: 0 0 0.5rem 0;
    padding: 0.75rem 1rem;
    font-size: 0.8125rem;
    color: #64748b;
    line-height: 1.5;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 0.5rem;
    border: 1px dashed #cbd5e1;
}

.emp-bulk-modal-hint-icon {
    margin-right: 0.25rem;
    color: #f59e0b;
}

.emp-bulk-modal-actions {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    margin-bottom: 0;
}

@media (min-width: 400px) {
    .emp-bulk-modal-actions {
        flex-direction: row;
        gap: 0.75rem;
    }
}

.emp-bulk-modal-btn {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    border-radius: 0.625rem;
    border: none;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.emp-bulk-modal-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
}

.emp-bulk-modal-btn-primary {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    color: #fff;
    box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35);
}

.emp-bulk-modal-btn-primary:not(:disabled):hover {
    background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
    box-shadow: 0 6px 20px rgba(37, 99, 235, 0.45);
    transform: translateY(-1px);
}

.emp-bulk-modal-btn-secondary {
    background: #fff;
    color: #334155;
    border: 2px solid #e2e8f0;
}

.emp-bulk-modal-btn-secondary:not(:disabled):hover {
    border-color: #cbd5e1;
    background: #f8fafc;
}

.mr-2 {
    margin-right: 0.5rem;
}
</style>
